import { UseQueryOptions } from '@tanstack/react-query';

export enum QueryKey {
	Bonus = 'bonus',
	CatalogItem = 'catalog-item',
	CatalogsGridwall = 'catalogs-gridwall',
	CatalogsAllowanceBoost = 'catalogs-allowance-boost',
	CompanyItemCategories = 'company-item-categories',
	CompanyItemDetails = 'company-item-details',
	CompanyItems = 'company-items',
	CurrencyConversionRate = 'currency-conversion-rate',
	FeaturedItems = 'featured-items',
	Reward = 'reward',
}

export type QueryOptions<T = unknown> = Omit<UseQueryOptions<T, unknown, T>, 'queryFn' | 'queryKey'>;

export enum Provider {
	Amazon = 'amazon',
	Axomo = 'axomo',
	Bonusly = 'bonusly', // allowance boost
	BulkDonation = 'bulk_donation',
	Cadooz = 'cadooz', // deprecated
	Paypal = 'paypal', // deprecated
	Runa = 'runa',
	Tango = 'tango_card',
	Tremendous = 'tremendous',
}

export enum Catalog {
	ShopAll = 'all_categories',
}

export type CatalogItem = {
	id: string;
	archivedAt?: string | null;
	autoclaimable: boolean;
	brandName: string;
	categories?: Category[];
	claimUrl?: string | null;
	countries?: Country[];
	createdAt: string;
	currency?: Currency | null;
	denominations?: Denomination[];
	description?: string | null;
	distributionMethod: DistributionMethod;
	externalIdentifier?: string | null;
	feePercentage: number;
	filters?: string | null;
	fullName: string;
	itemType: ItemType;
	legacyId: string;
	logo: Logo;
	promoted: boolean;
	provider?: Provider | null;
	redemptionLocation?: string | null;
	redemptionMethod?: RedemptionMethod | null;
	showButtonAndInstructions: boolean;
	state: ItemState;
	warnings: string[];
	website?: string | null;
};

export type Category = {
	id: string;
	key: string;
	name: string;
};

export interface ClaimedRewardSummary {
	id: string;
	categories: string[];
	item: CatalogItem;
	recommended_reason: 'promoted' | 'personal_recommendation' | '';
}

export type CompanyItem = {
	id: string;
	archivedAt?: string | null;
	companyId: string;
	item: CatalogItem;
	legacyId?: string | null;
	state: ItemState;
};

export type CompanyItemCategory = {
	id: string;
	key: string;
	name: string;
};

export type CompanyItemDetailsResponse = {
	data: {
		id: string;
		archivedAt?: string | null;
		companyId: string;
		item: CatalogItem;
		state: ItemState;
	};
	meta: {
		pagination: PaginationParams;
	};
};

export type CompanyItemsResponse = {
	data: CompanyItem[];
	meta: {
		pagination: PaginationParams;
	};
};

export type CompanyItemCategoriesResponse = {
	meta: {
		exclusive_items: boolean;
		featured_items: boolean;
		pagination: PaginationParams;
		swag_items: boolean;
	};
	data: CompanyItemCategory[];
};

export type Country = {
	id: string;
	isoAlpha2Code: string;
	name: string;
};

type CredentialItem = {
	credentialType: string;
	label: string;
	type: string;
	value: string;
};

// NOTE: these come from Tango
export enum CredentialType {
	ByPassUrl = 'bypassUrl',
	CardCode = 'cardCode',
	CardNumber = 'cardNumber',
	Cvc2 = 'cvc2',
	LandingPage = 'landingPage',
	Pin = 'pin',
	RedemptionUrl = 'redemptionUrl',
	RewardSecurityCode = 'rewardSecurityCode',
	RewardSecurityUrl = 'rewardSecurityUrl',
	SecretCode = 'secretCode',
}

export type Currency = {
	id: string;
	exponent: number;
	iso4217Code: string;
	name: string;
	symbol: string;
};

export type CurrencyConversionRateResponse = {
	result: {
		conversion_rate: number;
	};
	success: boolean;
};

export type Denomination = {
	id: string;
	archivedAt?: string | null;
	externalIdentifier?: string | null;
	inactiveReasons: DenominationInactiveReasons[] | [];
	item?: CatalogItem;
	itemId?: string;
	legacyId: string;
	priceInSubunits: number;
	quantity?: number | null;
	state: DenominationState;
};

export enum DenominationInactiveReasons {
	AdminDisabled = 'admin_disabled',
	Discontinued = 'discontinued',
	NeedsAttention = 'needs_attention',
	OutOfStock = 'out_of_stock',
	SystemDisabled = 'system_disabled',
}

export enum DenominationState {
	Active = 'active',
	Archived = 'archived',
	Inactive = 'inactive',
}

export enum DistributionMethod {
	Axomo = 'axomo',
	CashOut = 'cash_out',
	Custom = 'custom',
	Donation = 'donation',
	GiftCard = 'gift_card',
}

export type GiftDetails = {
	giftEmail?: string;
	giftMessage?: string;
	isGift: boolean;
};

export enum ItemState {
	Active = 'active',
	Archived = 'archived',
	Inactive = 'inactive',
}

export enum ItemType {
	Core = 'core',
	Exclusive = 'exclusive',
	Swag = 'swag',
}

export type LegacyCatalogItem = {
	id: string;
	can_redeem: boolean;
	error?: string;
};

export type Logo = {
	id: string;
	altText: string;
	imageContentType: string;
	imageName: string;
	imageUrl: string;
};

export type PaginationParams = {
	enabled: 'false' | 'true';
	page_count: number;
	page_next: number | null;
	page_number: number;
	page_previous: number | null;
	page_size?: number;
	total_count: number;
};

export type RecognizeError = {
	response: {
		data: {
			message: string;
			success: boolean;
		};
	};
};

export type RedemptionMethod = {
	id: string;
	instructionParams: string[];
	instructions: string;
	key: string;
	name: string;
};

export type RedemptionRecord = {
	id: string;
	code?: string | null;
	credentialList?: CredentialItem[];
	expirationDate?: string | null;
	number?: number | null;
	orderId: string;
	pin?: number | null;
	redemptionUrl?: string | null;
	state: RedemptionState;
};

export enum RedemptionState {
	Canceled = 'canceled',
	Expired = 'expired',
	Failed = 'failed',
	ManualFulfilled = 'manual_fulfilled',
	ManualPending = 'manual_pending',
	New = 'new',
	OutOfStock = 'out_of_stock',
	PendingVoucher = 'pending_voucher',
	Retry = 'retry',
	Succeeded = 'succeeded',
}

export type Reward = {
	id: string;
	approvalTime?: string | null;
	approvedAt?: string | null;
	approvedBy?: string | null;
	approvingAdmins?: string | null;
	autoApprovable: boolean;
	createdAt: string;
	gift: boolean;
	giftEmail?: string;
	redemptionRecord?: RedemptionRecord | null;
	state: RewardState;
};

export type RewardRedemption = {
	redemption: {
		id: string;
		created_at: string;
		state: RedemptionState;
		certificate_url: string;
		claim_url?: string | null;
		auto_approvable: boolean;
		reward_details: {
			id: string;
			name: string;
			price: number;
			display_price: string;
			available: boolean;
			type: DistributionMethod;
			image_url: string;
			description: {
				text: string;
				html: string;
			};
			disclaimer_html: string | null;
			warning: string | null;
			quantity: string | null;
		};
	};
};

export type RewardResult = {
	id: string;
	name: string;
};

export enum RewardState {
	Approved = 'approved',
	Declined = 'declined',
	Failed = 'failed',
	Fulfilled = 'fulfilled',
	New = 'new',
	Refunded = 'refunded',
}

export interface RewardSummary {
	categories: string[];
	id: string;
	item: CatalogItem;
	recommended_reason: 'promoted' | 'personal_recommendation' | '';
}

export interface PopularRewardSummary extends RewardSummary {
	brand_name: string;
	image_url: string;
	reward_url: string;
}

export interface RewardsSummary {
	claimed: string;
	popular: string;
	promoted: string;
}
